import { memo, useLayoutEffect, useRef } from 'react'
import { TransformKind, type Document, type ImageNode } from '@/core/document'
import type { SourceImage } from '@/core/image'
import { renderNode } from '@/core/render/renderNode'
import { cn } from '@/lib/utils'

function imageRenderKey(image: ImageNode): string {
  const crop = image.transforms[TransformKind.Crop]
  if (!crop) throw new Error(`Photo thumbnail requires a crop for image "${image.id}"`)
  const orientation = image.transforms[TransformKind.Orientation]
  const adjustments = image.transforms[TransformKind.Adjustments]
  return [
    image.id,
    image.sourceId,
    crop.rect.x,
    crop.rect.y,
    crop.rect.width,
    crop.rect.height,
    orientation?.quarterTurns ?? 0,
    orientation?.flipHorizontal ?? false,
    orientation?.flipVertical ?? false,
    orientation?.straightenDeg ?? 0,
    adjustments?.brightness ?? 0,
    adjustments?.contrast ?? 0,
    adjustments?.saturation ?? 0,
  ].join(':')
}

interface PhotoThumbnailProps {
  document: Document
  image: ImageNode
  source: SourceImage
  size?: number
  className?: string
}

export const PhotoThumbnail = memo(function PhotoThumbnail({
  document,
  image,
  source,
  size = 24,
  className,
}: PhotoThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  if (!Number.isFinite(size) || size <= 0) throw new Error('Photo thumbnail requires a finite positive size')

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) throw new Error('Photo thumbnail canvas is missing')
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Photo thumbnail requires a 2D canvas context')
    const scale = window.devicePixelRatio || 1
    canvas.width = Math.round(size * scale)
    canvas.height = Math.round(size * scale)
    context.setTransform(scale, 0, 0, scale, 0, 0)
    context.clearRect(0, 0, size, size)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    renderNode(
      image,
      document,
      new Map([[image.sourceId, { ...source, bitmap: source.previewBitmap }]]),
      { width: size, height: size },
      context,
    )
  }, [document, image, size, source])

  return <canvas ref={canvasRef} className={cn('rounded-sm border', className)} style={{ width: size, height: size }} aria-hidden="true" />
}, (previous, next) => (
  imageRenderKey(previous.image) === imageRenderKey(next.image)
  && previous.source.previewBitmap === next.source.previewBitmap
  && previous.size === next.size
  && previous.className === next.className
))
